import { Fragment, memo } from 'react';

import { WorkspaceAgentThumbnailRow } from '~/components/v2/components/workspace/workspace-agent-panel/design-system/cards/WorkspaceAgentThumbnailRow';
import { type WorkspaceAgentThumbnail } from '~/components/v2/components/workspace/workspace-agent-panel/design-system/cards/workspaceAgentThumbnailTypes';
import { WorkspaceAgentPersonChip } from '~/components/v2/components/workspace/workspace-agent-panel/design-system/chips/WorkspaceAgentPersonChip';
import { WorkspaceAgentTagChip } from '~/components/v2/components/workspace/workspace-agent-panel/design-system/chips/WorkspaceAgentTagChip';
import {
  type WorkspaceAgentBulletSegment,
  type WorkspaceAgentSummaryBulletQuote,
} from '~/components/v2/components/workspace/workspace-agent-panel/design-system/summary/types';
import { WorkspaceAgentSummaryBulletQuoteCard } from '~/components/v2/components/workspace/workspace-agent-panel/design-system/summary/WorkspaceAgentSummaryBulletQuoteCard';

const isChipSegment = (segment: WorkspaceAgentBulletSegment) => segment.type === 'person' || segment.type === 'tag';

/** The copy a segment contributes to the sentence, or `null` for the segments that render as chips. */
const textOf = (segment: WorkspaceAgentBulletSegment) =>
  segment.type === 'text' || segment.type === 'emphasis' ? segment.text : null;

/**
 * Whether a space has to be invented between two segments.
 *
 * Prose carries its own spacing, so text meeting text is left alone; chips do not, and would
 * otherwise render as one unbroken bar of pills. Deriving the space here rather than asking the
 * caller to bake a trailing one into every string means a response that omits it still reads
 * correctly — and the space doubles as the line-break opportunity a run of chips needs to wrap.
 */
const needsSeparatorBefore = (
  previous: WorkspaceAgentBulletSegment | undefined,
  current: WorkspaceAgentBulletSegment,
) => {
  if (!previous) {
    return false;
  }

  const previousText = textOf(previous);
  const currentText = textOf(current);

  if (previousText !== null && currentText !== null) {
    return false;
  }

  if (isChipSegment(previous) && isChipSegment(current)) {
    return true;
  }

  return previousText === null ? !/^\s/.test(currentText ?? '') : !/\s$/.test(previousText);
};

export type WorkspaceAgentSummaryBulletProps = {
  'data-testid'?: string;
  onOpenQuote?: (id: string) => void;
  onOpenThumbnail?: (id: string) => void;
  quotes?: WorkspaceAgentSummaryBulletQuote[];
  segments: WorkspaceAgentBulletSegment[];
  thumbnails?: WorkspaceAgentThumbnail[];
};

/**
 * One finding: the claim, then the evidence for it.
 *
 * The bullet owns the spacing between claim, quotes and thumbnails because that relationship is the
 * design — a quote sitting closer to its claim than to the next bullet is what makes a stack of
 * these readable.
 */
export const WorkspaceAgentSummaryBullet = memo(
  ({
    'data-testid': testId,
    onOpenQuote,
    onOpenThumbnail,
    quotes,
    segments,
    thumbnails,
  }: WorkspaceAgentSummaryBulletProps) => (
    <div className="flex gap-2 text-14 text-gray-12 leading-5" data-testid={testId}>
      <span aria-hidden className="shrink-0">
        •
      </span>
      <div className="min-w-0 flex-1">
        <p>
          {segments.map((segment, index) => {
            const key = `${segment.type}-${index}`;
            const separator = needsSeparatorBefore(segments[index - 1], segment) ? ' ' : null;

            switch (segment.type) {
              case 'emphasis': {
                return (
                  <Fragment key={key}>
                    {separator}
                    <em className="font-medium not-italic">{segment.text}</em>
                  </Fragment>
                );
              }
              case 'person': {
                return (
                  <Fragment key={key}>
                    {separator}
                    <WorkspaceAgentPersonChip avatarUrl={segment.avatarUrl} name={segment.name} />
                  </Fragment>
                );
              }
              case 'tag': {
                return (
                  <Fragment key={key}>
                    {separator}
                    <WorkspaceAgentTagChip label={segment.label} />
                  </Fragment>
                );
              }
              default: {
                return (
                  <Fragment key={key}>
                    {separator}
                    {segment.text}
                  </Fragment>
                );
              }
            }
          })}
        </p>

        {quotes?.length ? (
          <div className="mt-1.5 space-y-1">
            {quotes.map((quote) => (
              <WorkspaceAgentSummaryBulletQuoteCard key={quote.id} onOpen={onOpenQuote} quote={quote} />
            ))}
          </div>
        ) : null}

        {thumbnails?.length ? (
          <div className="mt-1">
            <WorkspaceAgentThumbnailRow onOpen={onOpenThumbnail} thumbnails={thumbnails} />
          </div>
        ) : null}
      </div>
    </div>
  ),
);

WorkspaceAgentSummaryBullet.displayName = 'WorkspaceAgentSummaryBullet';
